import BrowserCell from 'components/BrowserCell/BrowserCell.react';
import { dateStringUTC } from 'lib/DateUtils';
import * as browserUtils from 'lib/browserUtils';
import DataBrowserHeaderBar from 'components/DataBrowserHeaderBar/DataBrowserHeaderBar.react';
import Editor from 'dashboard/Data/Browser/Editor.react';
import EmptyState from 'components/EmptyState/EmptyState.react';
import PreLoader from 'components/PreLoader/PreLoader.react';
import Parse from 'parse';
import React from 'react';
import styles from 'dashboard/Data/Browser/Browser.scss';
import Button from 'components/Button/Button.react';

const MAX_ROWS = 60; // Number of rows to render at any time
const ROW_HEIGHT = 31;

const READ_ONLY = ['objectId', 'createdAt', 'updatedAt', 'expiresAt'];

export default class BrowserTable extends React.Component {
  constructor(props) {
    super();

    this.state = {
      offset: 0,
    };
  }

  componentDidMount() {
    this.handleScroll = this.handleScroll.bind(this);
    this.refs.table.addEventListener('scroll', this.handleScroll);
  }

  componentWillReceiveProps(props, context) {
    if (props.className !== this.props.className) {
      this.setState({ offset: 0 });
      this.refs.table.scrollTop = 0;
    }
    else if (this.props.newObject !== props.newObject) {
      this.setState({ offset: 0 });
      this.refs.table.scrollTop = 0;
    }
    else if (this.props.ordering !== props.ordering) {
      this.setState({ offset: 0 });
      this.refs.table.scrollTop = 0;
    }
  }

  componentWillUnmount() {
    this.refs.table.removeEventListener('scroll', this.handleScroll);
  }

  handleScroll() {
    if (!this.props.data || this.props.data.length === 0) {
      return;
    }

    requestAnimationFrame(() => {
      const rowsAbove = Math.floor(this.refs.table.scrollTop / ROW_HEIGHT);
      let offset = this.state.offset;
      if (rowsAbove - this.state.offset > 20) {
        offset = Math.floor(rowsAbove / 10) * 10 - 10;
      }
      else if (rowsAbove - this.state.offset < 10) {
        offset = Math.max(0, Math.floor(rowsAbove / 10) * 10 - 30);
      }
      if (this.state.offset !== offset) {
        this.setState({ offset });
      }
      if (this.props.maxFetched - offset < 100) {
        this.props.fetchNextPage();
      }
    });
  }

  render() {
    const { isRefreshing } = this.props;

    let addRow = null;
    let ordering = {};
    if (this.props.ordering) {
      if (this.props.ordering[0] === '-') {
        ordering = { col: this.props.ordering.substr(1), direction: 'descending' };
      }
      else {
        ordering = { col: this.props.ordering, direction: 'ascending' };
      }
    }

    const headers = this.props.order.map(({ name, width }, i) => ({
      width,
      name,
      type: this.props.columns[name].type,
      targetClass: this.props.columns[name].targetClass,
      order: ordering.col === name ? ordering.direction : null,
    }
    ));
    let editor = null;
    let overflow = false;
    let table = <div ref="table" />;
    if (this.props.data) {
      let rowWidth = 210;
      for (let i = 0; i < this.props.order.length; i++) {
        rowWidth += this.props.order[i].width;
      }
      let newRow = null;
      if (this.props.newObject && this.state.offset <= 0) {
        overflow = true;
        newRow = (
          <div className={styles.addNewRow}>
            {
              RenderRow({
                row: -1, obj: this.props.newObject, props: this.props, state: this.state,
              })
            }
          </div>
        );
      }
      const rows = [];
      const end = Math.min(this.state.offset + MAX_ROWS, this.props.data.length);
      for (let i = this.state.offset; i < end; i++) {
        const index = i - this.state.offset;
        const obj = this.props.data[i];
        rows[index] = RenderRow({
          row: i, obj, props: this.props, state: this.state,
        });
      }

      if (this.props.editing) {
        let visible = false;
        if (this.props.current) {
          if (this.props.current.row < 0 && this.state.offset === 0) {
            visible = true;
          }
          else if (this.props.current.row >= this.state.offset && this.props.current.row < end) {
            visible = true;
          }
        }
        if (visible) {
          const { name, width } = this.props.order[this.props.current.col];
          const { type, targetClass } = this.props.columns[name];
          let readonly = READ_ONLY.indexOf(name) > -1;
          if (name === 'sessionToken') {
            if (this.props.className === '_User' || this.props.className === '_Session') {
              readonly = true;
            }
          }
          const obj = this.props.current.row < 0 ? this.props.newObject : this.props.data[this.props.current.row];

          let value = (obj || {}).attributes ? obj.attributes[name] : undefined;
          if (name === 'objectId') {
            value = obj.id;
          } else if (name === 'ACL' && this.props.className === '_User' && !value) {
            value = new Parse.ACL({ '*': { read: true }, [obj.id]: { read: true, write: true } });
          } else if (name === 'password' && this.props.className === '_User') {
            value = '';
          } else if (type === 'Array') {
            if (value) {
              value = value.map(val => (val instanceof Parse.Object ? val.toPointer() : val));
            }
          }
          let wrapTop = Math.max(0, this.props.current.row * ROW_HEIGHT);
          if (this.props.current.row > -1 && this.props.newObject) {
            wrapTop += 60;
          }
          let wrapLeft = 30;
          for (let i = 0; i < this.props.current.col; i++) {
            wrapLeft += this.props.order[i].width;
          }

          const UpdatedAt =
            this.props.data.length > 0
              ?
              dateStringUTC(this.props.data[0].get('updatedAt'))
              : null;

          editor = (
            <Editor
              top={wrapTop}
              left={wrapLeft}
              type={type}
              UpdatedAt={UpdatedAt}
              dbClassName={this.props.className}
              dbColName={name}
              targetClass={targetClass}
              value={value}
              readonly={readonly}
              width={width}
              onCancel={() => this.props.setEditing(false)}
              onCommit={(newValue) => {
                let canEdit = false;

                // Booleans
                if (typeof value === 'boolean' && typeof newValue === 'boolean') {
                  if (value !== newValue) {
                    canEdit = true;
                  }
                }

                // value === undefined
                if (typeof value === 'undefined' && typeof newValue === 'boolean') {
                  canEdit = true;
                }

                // if new value is not undefined
                if (typeof value === 'undefined' && typeof newValue !== 'undefined') {
                  canEdit = true;
                }

                // Everything else
                if (typeof value !== 'undefined' && typeof newValue !== 'undefined') {
                  canEdit = true;
                }

                if (typeof value !== 'undefined' && typeof newValue === 'undefined') {
                  canEdit = true;
                }

                if (typeof value !== 'undefined' && typeof newValue !== 'undefined') {
                  // if (newValue === '' || (typeof newValue === 'object' && Object.keys(newValue).length === 0)) {
                  if (newValue === '' || (typeof newValue === 'object' && Object.keys(newValue).length === 0)) {
                    if (!(newValue instanceof Date)) {
                      canEdit = true;
                      newValue = undefined;
                    }
                  }
                }

                if (canEdit) {
                  this.props.updateRow(
                    this.props.current.row,
                    name,
                    newValue,
                  );
                }

                this.props.setEditing(false);
              }} />
          );
        }
      }

      if (this.props.relation) {
        addRow = (
          <div className={styles.addRow}>
            <Button
              onClick={this.props.onAddRow}
              color="green"
              value={`Create a ${this.props.relation.targetClassName} and attach`} />
            {' '}
            <Button
              onClick={this.props.onAttachRows}
              color="green"
              value={`Attach existing rows from ${this.props.relation.targetClassName}`} />
          </div>
        );
      }

      if (this.props.newObject || this.props.data.length > 0) {
        table = (
          <div className={[styles.table, isRefreshing ? styles.isRefreshing : '', overflow ? styles.overflow : null].join(' ')} ref="table" style={{ minWidth: rowWidth }}>
            {newRow}
            {addRow}
            <div className={styles.rowsHolder} style={{ top: Math.max(0, this.state.offset * ROW_HEIGHT) }}>
              {rows}
            </div>
            {editor}
          </div>
        );
      }
      else {
        table = (
          <div className={[styles.table, isRefreshing ? styles.isRefreshing : ''].join(' ')} ref="table" style={{ minWidth: rowWidth }}>
            <div className={styles.empty}>
              {this.props.relation ?
                <EmptyState
                  title="No data to display"
                  description="This relation has no rows. Attach existing rows or create row."
                  cta={`Create ${this.props.relation.targetClassName} and attach`}
                  action={this.props.onAddRow}
                  secondaryCta={`Attach existing rows from ${this.props.relation.targetClassName}`}
                  secondaryAction={this.props.onAttachRows}
                  icon="files-solid" /> :
                <EmptyState
                  title="No data to display"
                  description="Add a row to store an object in this class."
                  icon="files-solid"
                  cta="Add a row"
                  action={this.props.onAddRow} />
              }
            </div>
          </div>
        );
      }
    }

    return (
      <div className={[styles.browser, addRow ? styles.hasAddRow : null, browserUtils.isSafari() ? styles.safari : ''].join(' ')}>
        <PreLoader
          loading={false}
          customPreLoaderClass={styles.customPreLoaderClass}
          customLoaderWrapperClass={styles.customLoaderWrapperClass}>
          {table}
          {addRow}
          <DataBrowserHeaderBar
            selected={this.props.selection['*']}
            selectAll={this.props.selectRow.bind(null, '*')}
            headers={headers}
            updateOrdering={this.props.updateOrdering}
            readonly={!!this.props.relation}
            handleDragDrop={this.props.handleHeaderDragDrop}
            onResize={this.props.handleResize}
            onAddColumn={this.props.onAddColumn} />
        </PreLoader>
      </div>
    );
  }
}


const RenderRow = ({
  row, obj, props, state,
}) => {
  const attributes = obj.attributes;
  const index = row - state.offset;
  return (
    <div key={`row${index}`} className={styles.tableRow}>
      <span className={styles.checkCell}>
        <input
          type="checkbox"
          checked={props.selection['*'] || props.selection[obj.id]}
          onChange={e => props.selectRow(obj.id, e.target.checked)} />
      </span>
      {props.order.map(({ name, width }, j) => {
        let type = props.columns[name].type;
        let readonly = READ_ONLY.indexOf(name) > -1;
        let attr = attributes[name];
        if (name === 'objectId') {
          attr = obj.id;
        }
        else if (name === 'ACL' && props.className === '_User' && !attr) {
          attr = new Parse.ACL({ '*': { read: true }, [obj.id]: { read: true, write: true } });
        }
        else if (type === 'Relation' && !attr && obj.id) {
          attr = new Parse.Relation(obj, name);
          attr.targetClassName = props.columns[name].targetClass;
        }
        else if (name === 'deviceToken' && props.className === '_Installation' && typeof attr === 'object') {
            type = 'Object';
          }
          const current = props.current && props.current.row === row && props.current.col === j;
          let hidden = false;
          if (name === 'password' && props.className === '_User') {
            hidden = true;
          }
        else if (name === 'sessionToken') {
          if (props.className === '_User' || props.className === '_Session') {
            hidden = true;
          }
        }
        else if (props.className === '_Session') {
          readonly = true;
          if (name === 'createdWidth') {
            readonly = false;
          }
        }

        return (
          <BrowserCell
            key={name}
            type={type}
            readonly={readonly}
            width={width}
            current={current}
            onSelect={() => props.setCurrent({ row, col: j })}
            onEditChange={state => props.setEditing(state)}
            onPointerClick={props.onPointerClick}
            setRelation={props.setRelation}
            value={attr}
            hidden={hidden} />
        );
      })}
    </div>
  );
};
